<script setup lang="ts">
import { completeNewCustomerTaskActionAsync } from '@user-center/utils/newCustomerTask'
import { $t } from '@user-center/utils/i18n'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const payload = ref<any>(null)
const completing = ref(false)
const visible = computed(() => Boolean(payload.value))
const nextTasks = computed(() => (payload.value?.activity?.taskList || [])
  .filter((item: any) => item.taskTemplateId !== payload.value?.task?.taskTemplateId && item.actionStatus !== 'COMPLETED')
  .slice(0, 3))

function close(): void {
  payload.value = null
}

function openTaskCenter(): void {
  close()
  void router.push('/collection/newCustomerTask')
}

async function useReward(): Promise<void> {
  if (completing.value)
    return
  completing.value = true
  try {
    await completeNewCustomerTaskActionAsync(
      payload.value?.eventType,
      payload.value?.bizNo,
      payload.value?.activityId,
    )
  }
  catch {
    // The reward usage route must remain available even when completion is delayed.
  }
  finally {
    completing.value = false
  }
  close()
  void router.push('/collection/package')
}

function onRewardPopup(event: Event): void {
  payload.value = (event as CustomEvent).detail || null
}

onMounted(() => {
  window.addEventListener('new-customer-task-reward-popup', onRewardPopup)
})

onBeforeUnmount(() => {
  window.removeEventListener('new-customer-task-reward-popup', onRewardPopup)
})
</script>

<template>
  <el-dialog v-if="visible" :model-value="visible" append-to-body width="440px" align-center :show-close="false" class="new-customer-task-reward-popup" @update:model-value="close" @close="close">
    <div class="new-customer-task-reward-popup__header">
      <button type="button" :aria-label="$t('userCenter.newCustomerTask.close')" @click="close">
        ×
      </button>
      <span>{{ $t('userCenter.newCustomerTask.benefit') }}</span>
      <h2>{{ payload?.activity?.benefitTitle || $t('userCenter.newCustomerTask.rewardUnlocked') }}</h2>
      <p>{{ $t('userCenter.newCustomerTask.rewardIssuing') }}</p>
    </div>
    <div class="new-customer-task-reward-popup__body">
      <div class="new-customer-task-reward-popup__reward">
        <b>¥</b>
        <div><strong>{{ payload?.task?.rewardText || $t('userCenter.newCustomerTask.exclusiveReward') }}</strong><small>{{ $t('userCenter.newCustomerTask.rewardIssuingHint') }}</small></div>
      </div>
      <div class="new-customer-task-reward-popup__actions">
        <el-button type="primary" :loading="completing" @click="useReward">
          {{ $t('userCenter.newCustomerTask.useNow') }}
        </el-button>
        <el-button @click="openTaskCenter">
          {{ $t('userCenter.newCustomerTask.viewDetails') }}
        </el-button>
      </div>
      <template v-if="nextTasks.length">
        <h3>{{ $t('userCenter.newCustomerTask.completeMore') }}</h3>
        <div v-for="task in nextTasks" :key="task.taskTemplateId" class="new-customer-task-reward-popup__task">
          <span>{{ task.title || $t('userCenter.newCustomerTask.task') }}</span><em>{{ task.actionText || $t('userCenter.newCustomerTask.goComplete') }}</em>
        </div>
      </template>
    </div>
  </el-dialog>
</template>

<style scoped lang="scss">
:deep(.new-customer-task-reward-popup) { max-width: calc(100vw - 32px); overflow: hidden; border-radius: 18px; box-shadow: 0 24px 64px rgba(27, 42, 63, .24); }
:deep(.new-customer-task-reward-popup .el-dialog__header) { display: none; }
:deep(.new-customer-task-reward-popup .el-dialog__body) { padding: 0; }
.new-customer-task-reward-popup__header { position: relative; padding: 28px 32px; color: #fff; background: linear-gradient(135deg, #ff9b3d, #ff542f); }
.new-customer-task-reward-popup__header button { position: absolute; top: 14px; right: 16px; width: 28px; height: 28px; padding: 0; color: #fff; font-size: 26px; line-height: 24px; cursor: pointer; background: rgba(255,255,255,.16); border: 0; border-radius: 50%; }
.new-customer-task-reward-popup__header span { display: inline-block; padding: 4px 10px; font-size: 13px; background: rgba(255,255,255,.18); border-radius: 999px; }.new-customer-task-reward-popup__header h2 { margin: 14px 0 7px; font-size: 25px; }.new-customer-task-reward-popup__header p { margin: 0; opacity: .9; }
.new-customer-task-reward-popup__body { padding: 24px 30px 28px; }.new-customer-task-reward-popup__reward { display: flex; overflow: hidden; border-radius: 10px; background: #fff4ed; }.new-customer-task-reward-popup__reward b { display: grid; width: 72px; place-items: center; color: #f25328; font-size: 30px; background: #ffe4d3; }.new-customer-task-reward-popup__reward div { padding: 13px 16px; }.new-customer-task-reward-popup__reward strong, .new-customer-task-reward-popup__reward small { display: block; }.new-customer-task-reward-popup__reward small { margin-top: 5px; color: #7b8794; font-size: 12px; }
.new-customer-task-reward-popup__actions { display: flex; gap: 12px; margin-top: 20px; }.new-customer-task-reward-popup__actions .el-button { flex: 1; height: 42px; margin: 0; }.new-customer-task-reward-popup__actions .el-button--primary { border: 0; background: linear-gradient(90deg, #ff7a20, #ff4e20); }
.new-customer-task-reward-popup__body h3 { margin: 24px 0 10px; font-size: 15px; }.new-customer-task-reward-popup__task { display: flex; justify-content: space-between; padding: 10px 12px; margin-top: 8px; color: #6c7888; font-size: 14px; background: #f7f8fa; border-radius: 8px; }.new-customer-task-reward-popup__task em { color: #f2662f; font-style: normal; }
</style>
